import { useCallback } from 'react';
import { endpoints, type DriveMedia } from '../services/api';
import { useSession } from '../store/session';
import { useSettings, type MediaItem } from '../store/settings';
import { usePolling } from './usePolling';

/**
 * Expands the chosen OneDrive folders (and, by default, all their sub-folders) into
 * slideshow items. Each item's URL is our own /api redirect, so short-lived Microsoft
 * download links are fetched fresh when shown.
 */
export function useCloudMedia(): MediaItem[] {
  const folders = useSettings((s) => s.media.cloudFolders);
  const quality = useSettings((s) => s.media.quality);
  const connected = useSession((s) => s.microsoft?.connected ?? false);
  const key =
    connected && folders.length
      ? `${quality}|${folders.map((f) => `${f.folderId}:${f.includeSubfolders !== false ? 1 : 0}`).join('|')}`
      : null;

  const load = useCallback(async () => {
    const results = await Promise.allSettled(
      folders.map((f) => endpoints.driveMedia(f.folderId, f.includeSubfolders !== false)),
    );
    const seen = new Set<string>();
    return results.flatMap((r) =>
      r.status === 'fulfilled'
        ? r.value.items
            // A folder and one of its sub-folders may both be chosen – show each photo once.
            .filter((m) => !seen.has(m.id) && seen.add(m.id))
            .map<MediaItem>((m) => ({
              id: `onedrive:${m.id}`,
              kind: m.kind,
              title: m.name,
              url: contentUrl(m, quality),
              width: m.width ?? null,
              height: m.height ?? null,
            }))
        : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return usePolling(key, load, 30 * 60_000).data ?? NONE;
}

function contentUrl(m: DriveMedia, quality: string): string {
  const q = new URLSearchParams({ kind: m.kind, quality });
  if (m.mime) q.set('mime', m.mime);
  return `/api/microsoft/drive/content/${encodeURIComponent(m.id)}?${q}`;
}

// Must be a stable reference: a fresh [] each render would change the media list every
// render and loop the queue's effects.
const NONE: MediaItem[] = [];
